import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Loader2, Stamp, CheckCircle2, XCircle, PartyPopper, Package, Clock, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast, Toaster } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_META = {
  solicitado: { label: 'Solicitud registrada — validá con el proveedor', color: 'bg-slate-100 text-slate-700', icon: Clock },
  pago_pendiente_colegio: { label: 'Validado — esperando pago del Colegio', color: 'bg-amber-100 text-amber-800', icon: Clock },
  pagado: { label: 'Pagado, esperando fabricación', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  en_produccion: { label: 'En fabricación', color: 'bg-purple-100 text-purple-800', icon: Package },
  listo_retiro: { label: '¡Listo para retirar!', color: 'bg-green-100 text-green-800', icon: Truck },
  entregado: { label: 'Entregado', color: 'bg-slate-200 text-slate-700', icon: CheckCircle2 },
};

export default function BeneficioSelloPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [solicitando, setSolicitando] = useState(false);
  const [data, setData] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Si el usuario no está logueado, redirigir directamente al login guardando
  // el retorno a /beneficio-sello para que el chequeo de elegibilidad se
  // dispare automáticamente después del login.
  useEffect(() => {
    if (!authLoading && !user) {
      sessionStorage.setItem('redirectAfterLogin', '/beneficio-sello');
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/sellos/mi-estado`, { headers });
      setData(r.data);
    } catch (err) {
      toast.error('No pudimos consultar tu beneficio: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) cargar();
    // eslint-disable-next-line
  }, [user]);

  const solicitar = async () => {
    if (!data?.evento?.id) return;
    setSolicitando(true);
    try {
      const r = await axios.post(
        `${API_URL}/sellos/solicitar`,
        { evento_id: data.evento.id },
        { headers },
      );
      toast.success('¡Sello solicitado! Guardá tu QR.');
      // Refresh para traer el pedido con access_token
      await cargar();
    } catch (err) {
      toast.error('Error solicitando el sello: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSolicitando(false);
    }
  };

  // Mientras verifica auth o mientras se dispara el redirect al login
  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-slate-500 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Verificando sesión…
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-slate-500 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Verificando tu elegibilidad…
      </div>
    );
  }

  // Ya solicitó → mostrar estado + QR
  if (data?.ya_solicitado && data?.pedido) {
    const pedido = data.pedido;
    const meta = ESTADO_META[pedido.estado] || ESTADO_META.solicitado;
    const Icon = meta.icon;
    const publicUrl = `${window.location.origin}/sello/${pedido.access_token}`;
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10 px-4">
        <Toaster position="top-center" />
        <div className="max-w-lg mx-auto">
          <Card data-testid="sello-pedido-card">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <Stamp className="w-10 h-10 text-purple-700" />
              </div>
              <CardTitle className="text-xl">Tu Sello Profesional</CardTitle>
              <CardDescription>
                Evento: <strong>{pedido.evento_nombre}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-center gap-2" data-testid="sello-estado-badge">
                <Badge className={`text-sm px-3 py-1 ${meta.color}`}>
                  <Icon className="w-4 h-4 mr-1 inline" />
                  {meta.label}
                </Badge>
              </div>

              <div className="flex flex-col items-center bg-slate-50 rounded-lg p-5 border">
                <p className="text-xs text-slate-500 mb-3 text-center">
                  Mostrá este código al proveedor cuando pases a retirar tu sello.
                </p>
                <QRCodeCanvas
                  value={publicUrl}
                  size={220}
                  level="M"
                  includeMargin
                  data-testid="sello-qr"
                />
                <p className="text-[10px] text-slate-400 mt-2 break-all text-center">
                  {publicUrl}
                </p>
              </div>

              <div className="text-sm text-slate-600 space-y-1">
                <p><strong>Matrícula:</strong> {pedido.matricula}</p>
                <p><strong>Titular:</strong> {pedido.nombre} {pedido.apellido}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // No elegible
  if (!data?.elegible) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2"><XCircle className="w-10 h-10 text-slate-400" /></div>
            <CardTitle className="text-lg">Sin eventos elegibles hoy</CardTitle>
            <CardDescription>
              El beneficio del sello profesional está disponible solo el día
              del evento y para eventos que lo incluyen expresamente. Volvé el
              día del próximo evento con beneficio activo.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Elegible → botón para solicitar
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-purple-50 to-white">
      <Toaster position="top-center" />
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            <PartyPopper className="w-12 h-12 text-purple-600" />
          </div>
          <CardTitle className="text-2xl">¡Tenés el beneficio disponible!</CardTitle>
          <CardDescription className="text-base mt-2">
            Estás inscripto/a a <strong>{data.evento?.nombre}</strong> y podés
            solicitar tu sello profesional bonificado. Presioná el botón para
            generar tu código de retiro.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button
            size="lg"
            onClick={solicitar}
            disabled={solicitando}
            className="bg-purple-600 hover:bg-purple-700 text-white text-base px-8 py-6"
            data-testid="btn-solicitar-sello"
          >
            {solicitando
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generando…</>
              : <><Stamp className="w-5 h-5 mr-2" /> Solicitar mi Sello Profesional</>}
          </Button>
          <p className="text-xs text-slate-500 mt-4">
            El pedido queda registrado y te avisamos por WhatsApp cuando esté listo para retirar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
